import { TextField } from '@fluentui/react';
import { Menu } from '@headlessui/react';
import {
    Checkbox,
    FormControlLabel,
    Radio,
    RadioGroup,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ROUTES } from '../../consts';
import { ReactComponent as EditIcon } from '../../images/icons/edit.svg';
import { ReactComponent as LayersIcon } from '../../images/icons/layers.svg';
import { ReactComponent as SearchIcon } from '../../images/icons/search.svg';
import { ReactComponent as TrashIcon } from '../../images/icons/trash.svg';
import { ISecurityUser, securityService, TUserMigrationStatus } from '../../services/security';

const userStatusStyles: Record<TUserMigrationStatus, { backgroundColor: string; color: string }> = {
    SSO: { backgroundColor: '#FEF3C7', color: '#92400E' },
    VCP: { backgroundColor: '#FED7AA', color: '#9A3412' },
    EXT: { backgroundColor: '#DCFCE7', color: '#166534' },
    INT: { backgroundColor: '#A7F3D0', color: '#065F46' },
    DIS: { backgroundColor: '#FEE2E2', color: '#991B1B' },
};

const flagStyles = {
    yes: { backgroundColor: '#DCFCE7', color: '#166534' },
    no: { backgroundColor: '#FEF3C7', color: '#92400E' },
};

const getUserKey = (user: ISecurityUser) => String(user.userPk ?? user.userId ?? user.email ?? '');

const userExistsInAzure = (user: ISecurityUser) =>
    user.existsInAzure === true || ['SSO', 'EXT', 'DIS'].includes(user.migrationStatus ?? '');

const userExistsInVcp = (user: ISecurityUser) =>
    ['VCP', 'EXT', 'INT', 'DIS'].includes(user.migrationStatus ?? '');

const useVendorName = (vendorId: string, enabled: boolean, initialVendorName = '') => {
    const [vendorName, setVendorName] = useState(initialVendorName);

    useEffect(() => {
        const normalizedVendorId = vendorId.trim();
        if (!enabled || !normalizedVendorId) {
            setVendorName('');
            return;
        }

        const timeoutId = window.setTimeout(() => {
            securityService
                .getVendorName(normalizedVendorId)
                .then((name) => setVendorName(name ?? ''))
                .catch(() => setVendorName(''));
        }, 400);

        return () => window.clearTimeout(timeoutId);
    }, [enabled, vendorId]);

    return vendorName;
};

const useSecurityUsers = () => {
    const [users, setUsers] = useState<ISecurityUser[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const getUsers = useCallback(() => {
        setIsLoading(true);
        securityService
            .getUsers()
            .then((data) => {
                setUsers(data);
            })
            .catch(() => {
                toast.error('Something went wrong! Please try again.');
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        getUsers();
    }, [getUsers]);

    return { users, isLoading, getUsers };
};

const UserManagement = () => {
    const { id } = useParams();
    const pathName = window.location.pathname;

    if (pathName.endsWith('/create')) {
        return <CreateUser />;
    }

    if (id) {
        return <EditUser id={id} />;
    }

    return <UserList />;
};

const Heading = () => {
    return (
        <div className="max-w-80 flex items-center gap-x-5 z-[9999]">
            <LayersIcon />
            <div className="flex flex-col gap-y-2 ">
                <h1 className="text-gray-900 text-2xl font-extrabold">Users</h1>
                <p className="text-gray-500 text-sm">
                    Updated on {moment(new Date()).format('DD MMM YYYY, HH:mm')}
                </p>
            </div>
        </div>
    );
};

const UserList = () => {
    const navigate = useNavigate();
    const { users, isLoading, getUsers } = useSecurityUsers();
    const [searchKey, setSearchKey] = useState('');
    const [appliedSearchKey, setAppliedSearchKey] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

    const filteredUsers = useMemo(() => {
        const normalizedSearchKey = appliedSearchKey.trim().toLowerCase();

        if (!normalizedSearchKey) {
            return users;
        }

        return users.filter((user) =>
            [
                user.migrationStatus,
                user.vendorDesc,
                user.userGroup?.vendorId,
                user.firstName,
                user.lastName,
                user.email,
            ]
                .join(' ')
                .toLowerCase()
                .includes(normalizedSearchKey),
        );
    }, [appliedSearchKey, users]);

    const onToggleUser = (userId: string) => {
        setSelectedUserIds((prev) =>
            prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
        );
    };

    return (
        <div className="py-10">
            <div className="py-6 px-8 flex items-center justify-between sticky top-[80px] bg-white z-10">
                <Heading />
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        setAppliedSearchKey(searchKey);
                    }}
                >
                    <div className="flex flex-wrap items-center gap-4">
                        <TextField
                            value={searchKey}
                            onChange={(_, value) => setSearchKey(value ?? '')}
                            placeholder="Search"
                            className="w-72"
                            styles={{
                                fieldGroup: {
                                    borderColor: '#D0D5DD',
                                    outline: 'none',
                                    height: 40,
                                },
                            }}
                        />
                        <button
                            type="submit"
                            className="bg-[#E7E8EC] px-4 py-2.5 flex gap-x-2 hover:bg-[#d3d3d8] transition"
                        >
                            <SearchIcon />
                            Search
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(`/${ROUTES.USER_MANAGEMENT}/create`)}
                            className="bg-black text-white px-4 py-2.5 flex gap-x-2 hover:opacity-80 transition"
                        >
                            New user
                        </button>
                    </div>
                </form>
            </div>
            <div className="mt-8 px-8">
                <Table
                    className="border-x rounded-lg"
                    style={{
                        boxShadow:
                            '0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.10)',
                    }}
                >
                    <TableHead className="bg-bg-gray py-10 sticky top-[188px] z-10">
                        <TableRow>
                            <TableCell className="border-none w-12"></TableCell>
                            <TableCell className="border-none">Migration Status</TableCell>
                            <TableCell className="border-none">Vendor name</TableCell>
                            <TableCell className="border-none">Vendor code</TableCell>
                            <TableCell className="border-none">First name</TableCell>
                            <TableCell className="border-none">Last name</TableCell>
                            <TableCell className="border-none">Email</TableCell>
                            <TableCell className="border-none w-28"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading &&
                            new Array(4).fill(0).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell colSpan={8}>
                                        <Skeleton className="w-full py-3" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        {!isLoading && filteredUsers.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} className="text-center text-gray-500">
                                    No users found
                                </TableCell>
                            </TableRow>
                        )}
                        {filteredUsers.map((user) => (
                            <UserRow
                                key={getUserKey(user)}
                                user={user}
                                checked={selectedUserIds.includes(getUserKey(user))}
                                onToggleUser={onToggleUser}
                                onDeleted={getUsers}
                            />
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};

interface IUserRowProps {
    user: ISecurityUser;
    checked: boolean;
    onToggleUser: (userId: string) => void;
    onDeleted: () => void;
}

const UserRow = ({ user, checked, onToggleUser, onDeleted }: IUserRowProps) => {
    const userKey = getUserKey(user);
    const migrationStatus = user.migrationStatus ?? 'VCP';

    return (
        <TableRow>
            <TableCell>
                <Checkbox checked={checked} onChange={() => onToggleUser(userKey)} />
            </TableCell>
            <TableCell style={userStatusStyles[migrationStatus]} className="font-bold">
                {migrationStatus}
            </TableCell>
            <TableCell>{user.vendorDesc ?? '-'}</TableCell>
            <TableCell>{user.userGroup?.vendorId ?? '-'}</TableCell>
            <TableCell>{user.firstName ?? '-'}</TableCell>
            <TableCell>{user.lastName ?? '-'}</TableCell>
            <TableCell>{user.email ?? '-'}</TableCell>
            <TableCell className="h-[80px]">
                <div className="flex items-center gap-x-1">
                    <Link
                        to={`/${ROUTES.USER_MANAGEMENT}/${userKey}`}
                        state={{ user }}
                        className="p-2.5 hover:bg-gray-100 rounded-md transition"
                    >
                        <EditIcon />
                    </Link>
                    <DeleteUserButton user={user} onDeleted={onDeleted} />
                </div>
            </TableCell>
        </TableRow>
    );
};

const DeleteUserButton = ({
    user,
    onDeleted,
    variant = 'icon',
}: {
    user: ISecurityUser;
    onDeleted: () => void;
    variant?: 'icon' | 'text';
}) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const userKey = getUserKey(user);

    const deleteUser = () => {
        setIsDeleting(true);
        securityService
            .deleteUser(userKey)
            .then(() => {
                toast.success('User deleted successfully.');
                onDeleted();
            })
            .catch(() => {
                toast.error('Unable to delete the user. Please try again.');
            })
            .finally(() => {
                setIsDeleting(false);
            });
    };

    return (
        <Menu as="div" className="relative inline-block text-left">
            <Menu.Button
                type="button"
                disabled={isDeleting || !userKey}
                aria-label={`Delete ${user.email ?? 'user'}`}
                className={
                    variant === 'text'
                        ? 'bg-red-600 text-white px-4 py-2.5 hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed'
                        : 'p-2.5 hover:bg-gray-100 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed'
                }
            >
                {isDeleting ? 'Deleting...' : variant === 'text' ? 'Delete user' : <TrashIcon />}
            </Menu.Button>
            <Menu.Items className="absolute right-0 mt-2 w-72 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none z-[40]">
                <div className="px-4 py-3">
                    <Menu.Item disabled>
                        {() => (
                            <p className="text-sm text-gray-700">
                                Are you sure to delete the user?
                            </p>
                        )}
                    </Menu.Item>
                    <Menu.Item>
                        {() => (
                            <button
                                type="button"
                                onClick={deleteUser}
                                disabled={isDeleting}
                                className="mt-3 w-full rounded-md bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Confirm Delete
                            </button>
                        )}
                    </Menu.Item>
                </div>
            </Menu.Items>
        </Menu>
    );
};

const EditUser = ({ id }: { id: string }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const userFromState = (location.state as { user?: ISecurityUser } | null)?.user;
    const user = userFromState ?? securityService.getCachedUser(id);
    const [currentUser, setCurrentUser] = useState(user);
    const [firstName, setFirstName] = useState(user?.firstName ?? '');
    const [lastName, setLastName] = useState(user?.lastName ?? '');
    const [email] = useState(user?.email ?? '');
    const [vendorId, setVendorId] = useState(user?.userGroup?.vendorId ?? '');
    const [groupId, setGroupId] = useState(user?.userGroup?.groupId ?? 2);
    const [initialPassword, setInitialPassword] = useState('');
    const [resetPassword, setResetPassword] = useState(false);
    const [forceChangePasswordNextSignIn, setForceChangePasswordNextSignIn] = useState(false);
    const [creatingTarget, setCreatingTarget] = useState<'azure' | 'vcp' | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [statusAction, setStatusAction] = useState<'enable' | 'disable' | null>(null);
    const vendorName = useVendorName(vendorId, groupId === 2, user?.vendorDesc ?? '');

    if (!currentUser) {
        return (
            <UserFormLayout title="Edit user">
                <div className="text-gray-500">
                    User not found in the current table response. Please return to users and open
                    edit from the table.
                </div>
            </UserFormLayout>
        );
    }

    const existsInAzure = userExistsInAzure(currentUser);
    const existsInVcp = userExistsInVcp(currentUser);
    const isFrameBrandUser = email.trim().toLowerCase().endsWith('@frame-brand.com');
    const shouldCreateAzureUser = !existsInAzure && !isFrameBrandUser;
    const needsExternalVcpUser = !existsInVcp && groupId === 2;
    const userKey = getUserKey(currentUser);

    const createMissingUser = (target: 'azure' | 'vcp') => {
        if (!email.trim()) {
            toast.error('Email is required.');
            return;
        }

        if (target === 'azure' && !shouldCreateAzureUser) {
            toast.error('Azure B2C user is not created for frame-brand.com users.');
            return;
        }

        if (target === 'azure' && !initialPassword) {
            toast.error('Initial password is required to create an Azure user.');
            return;
        }

        if (target === 'vcp' && needsExternalVcpUser && !vendorId.trim()) {
            toast.error('Vendor code is required to create an external VCP user.');
            return;
        }

        setCreatingTarget(target);
        securityService
            .createUser({
                email: email.trim(),
                firstName: firstName.trim() || undefined,
                lastName: lastName.trim() || undefined,
                vendorId:
                    target === 'vcp' && needsExternalVcpUser
                        ? vendorId.trim() || undefined
                        : undefined,
                groupId: target === 'vcp' ? groupId : currentUser.userGroup?.groupId ?? groupId,
                initialPassword: target === 'azure' ? initialPassword || undefined : undefined,
                forceChangePasswordNextSignIn:
                    target === 'azure' ? forceChangePasswordNextSignIn : false,
            })
            .then((createdUser) => {
                setCurrentUser(createdUser);
                setVendorId(createdUser.userGroup?.vendorId ?? vendorId);
                setGroupId(createdUser.userGroup?.groupId ?? groupId);
                toast.success('User created successfully.');
            })
            .catch(() => toast.error('Unable to create the user. Please try again.'))
            .finally(() => setCreatingTarget(null));
    };

    const saveUser = () => {
        if (!currentUser.userId) {
            toast.error('VCP user must exist before saving.');
            return;
        }

        if (groupId === 2 && !vendorId.trim()) {
            toast.error('Vendor code is required for an external user.');
            return;
        }

        if (resetPassword && !initialPassword.trim()) {
            toast.error('Initial password is required when resetting password.');
            return;
        }

        setIsSaving(true);
        securityService
            .updateUser({
                userId: currentUser.userId,
                email: email.trim(),
                firstName: firstName.trim() || undefined,
                lastName: lastName.trim() || undefined,
                groupId,
                vendorId: groupId === 2 ? vendorId.trim() || undefined : undefined,
                resetPassword,
                initialPassword: resetPassword ? initialPassword : undefined,
                forceChangePasswordNextSignIn: resetPassword
                    ? forceChangePasswordNextSignIn
                    : false,
            })
            .then(() => {
                const updatedVendorId = groupId === 2 ? vendorId : '';
                setVendorId(updatedVendorId);
                setCurrentUser({
                    ...currentUser,
                    firstName,
                    lastName,
                    email,
                    userGroup: {
                        ...currentUser.userGroup,
                        groupId,
                        vendorId: updatedVendorId || undefined,
                    },
                    migrationStatus: groupId === 1 ? 'INT' : currentUser.migrationStatus,
                });
                setResetPassword(false);
                setInitialPassword('');
                setForceChangePasswordNextSignIn(false);
                toast.success('User updated successfully.');
            })
            .catch(() => toast.error('Unable to update the user. Please try again.'))
            .finally(() => setIsSaving(false));
    };

    const setUserEnabled = (enabled: boolean) => {
        if (!userKey) {
            toast.error('User key was not found.');
            return;
        }

        const action = enabled ? 'enable' : 'disable';
        setStatusAction(action);
        const request = enabled
            ? securityService.enableUser(userKey)
            : securityService.disableUser(userKey);
        request
            .then(() => {
                setCurrentUser({
                    ...currentUser,
                    migrationStatus: enabled ? 'EXT' : 'DIS',
                });
                toast.success(
                    enabled ? 'User enabled successfully.' : 'User disabled successfully.',
                );
            })
            .catch(() => toast.error('Unable to update the user status. Please try again.'))
            .finally(() => setStatusAction(null));
    };

    return (
        <UserFormLayout title="Edit user">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
                <UserTextField label="First name" value={firstName} onChange={setFirstName} />
                <UserTextField label="Last name" value={lastName} onChange={setLastName} />
                <UserTextField label="Email" value={email} disabled />
                {groupId === 2 && (
                    <UserTextField label="Vendor code" value={vendorId} onChange={setVendorId} />
                )}
                <UserTextField label="Vendor name" value={vendorName} disabled />
            </div>
            <RadioGroup
                row
                value={String(groupId)}
                onChange={(event) => setGroupId(Number(event.target.value))}
            >
                <FormControlLabel value="1" control={<Radio />} label="Internal" />
                <FormControlLabel value="2" control={<Radio />} label="External" />
            </RadioGroup>
            <div className="max-w-4xl">
                <label className="flex items-center gap-2">
                    <Checkbox
                        checked={resetPassword}
                        onChange={(_, checked) => {
                            const shouldResetPassword = checked ?? false;
                            setResetPassword(shouldResetPassword);
                            if (!shouldResetPassword) {
                                setInitialPassword('');
                                setForceChangePasswordNextSignIn(false);
                            }
                        }}
                    />
                    Reset password
                </label>
            </div>
            {resetPassword && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
                    <TextField
                        label="Initial password"
                        type="password"
                        value={initialPassword}
                        placeholder="Example: Abcd1234"
                        onChange={(_, value) => setInitialPassword(value ?? '')}
                    />
                    <label className="flex items-center gap-2 self-end">
                        <Checkbox
                            checked={forceChangePasswordNextSignIn}
                            onChange={(_, checked) =>
                                setForceChangePasswordNextSignIn(checked ?? false)
                            }
                        />
                        Force password change at next sign-in
                    </label>
                </div>
            )}
            {shouldCreateAzureUser && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
                    <TextField
                        label="Initial password"
                        type="password"
                        value={initialPassword}
                        placeholder="Example: Abcd1234"
                        onChange={(_, value) => setInitialPassword(value ?? '')}
                    />
                    <label className="flex items-center gap-2 self-end">
                        <Checkbox
                            checked={forceChangePasswordNextSignIn}
                            onChange={(_, checked) =>
                                setForceChangePasswordNextSignIn(checked ?? false)
                            }
                        />
                        Force password change at next sign-in
                    </label>
                </div>
            )}
            <ExistenceTable
                user={currentUser}
                onCreateAzure={() => createMissingUser('azure')}
                onCreateVcp={() => createMissingUser('vcp')}
                creatingTarget={creatingTarget}
            />
            <div className="flex flex-wrap items-center gap-4">
                <button
                    type="button"
                    onClick={saveUser}
                    disabled={isSaving}
                    className="bg-black text-white px-4 py-2.5 hover:opacity-80 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSaving ? 'Saving...' : 'Save user'}
                </button>
                <button
                    type="button"
                    onClick={() => setUserEnabled(true)}
                    disabled={statusAction === 'enable'}
                    className="bg-[#E7E8EC] px-4 py-2.5 hover:bg-[#d3d3d8] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {statusAction === 'enable' ? 'Enabling...' : 'Enable user'}
                </button>
                <button
                    type="button"
                    onClick={() => setUserEnabled(false)}
                    disabled={statusAction === 'disable'}
                    className="bg-[#E7E8EC] px-4 py-2.5 hover:bg-[#d3d3d8] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {statusAction === 'disable' ? 'Disabling...' : 'Disable user'}
                </button>
                <DeleteUserButton
                    user={currentUser}
                    variant="text"
                    onDeleted={() => navigate(`/${ROUTES.USER_MANAGEMENT}`)}
                />
            </div>
        </UserFormLayout>
    );
};

const CreateUser = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [vendorId, setVendorId] = useState('');
    const [initialPassword, setInitialPassword] = useState('');
    const [forceChangePasswordNextSignIn, setForceChangePasswordNextSignIn] = useState(false);
    const [groupId, setGroupId] = useState(2);
    const [usersFound, setUsersFound] = useState<ISecurityUser[] | null>(null);
    const [isChecking, setIsChecking] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const foundUser = usersFound?.[0];
    const vendorName = useVendorName(vendorId, groupId === 2, foundUser?.vendorDesc ?? '');
    const existsInAzure = foundUser ? userExistsInAzure(foundUser) : false;
    const existsInVcp = foundUser ? userExistsInVcp(foundUser) : false;
    const needsAzureUser = !existsInAzure;
    const needsVcpUser = !existsInVcp;
    const isFrameBrandUser = email.trim().toLowerCase().endsWith('@frame-brand.com');
    const shouldCreateAzureUser = needsAzureUser && !isFrameBrandUser;
    const needsExternalVcpUser = needsVcpUser && groupId === 2;

    const checkEmail = () => {
        const normalizedEmail = email.trim();
        if (!normalizedEmail) {
            toast.error('Enter an email address first.');
            return;
        }

        setIsChecking(true);
        securityService
            .getUsers(normalizedEmail)
            .then((users) => {
                setUsersFound(users);
                const user = users[0];
                if (user) {
                    setFirstName(user.firstName ?? '');
                    setLastName(user.lastName ?? '');
                    setVendorId(user.userGroup?.vendorId ?? '');
                } else {
                    setFirstName('');
                    setLastName('');
                    setVendorId('');
                    setInitialPassword('');
                    setForceChangePasswordNextSignIn(false);
                }
            })
            .catch(() => toast.error('Unable to check the user. Please try again.'))
            .finally(() => setIsChecking(false));
    };

    const createUser = () => {
        if (!email.trim()) {
            toast.error('Email is required.');
            return;
        }

        if (needsExternalVcpUser && !vendorId.trim()) {
            toast.error('Vendor code is required to create an external VCP user.');
            return;
        }

        if (shouldCreateAzureUser && !initialPassword) {
            toast.error('Initial password is required to create an Azure user.');
            return;
        }

        setIsCreating(true);
        securityService
            .createUser({
                email: email.trim(),
                firstName: firstName.trim() || undefined,
                lastName: lastName.trim() || undefined,
                vendorId: needsExternalVcpUser ? vendorId.trim() || undefined : undefined,
                groupId,
                initialPassword: initialPassword || undefined,
                forceChangePasswordNextSignIn,
            })
            .then(() => {
                toast.success('User created successfully.');
                navigate(`/${ROUTES.USER_MANAGEMENT}`);
            })
            .catch(() => toast.error('Unable to create the user. Please try again.'))
            .finally(() => setIsCreating(false));
    };

    return (
        <UserFormLayout title="Create user">
            <div className="flex flex-wrap items-end gap-4 max-w-4xl">
                <TextField
                    label="Email"
                    value={email}
                    onChange={(_, value) => setEmail(value ?? '')}
                    className="w-80"
                />
                <button
                    type="button"
                    onClick={checkEmail}
                    disabled={isChecking}
                    className="bg-[#E7E8EC] px-4 py-2.5 hover:bg-[#d3d3d8] transition disabled:opacity-50"
                >
                    {isChecking ? 'Checking...' : 'Check user'}
                </button>
            </div>

            <RadioGroup
                row
                value={String(groupId)}
                onChange={(event) => setGroupId(Number(event.target.value))}
            >
                <FormControlLabel value="1" control={<Radio />} label="Internal" />
                <FormControlLabel value="2" control={<Radio />} label="External" />
            </RadioGroup>

            {usersFound !== null && (
                <Table className="border-x rounded-lg max-w-6xl">
                    <TableHead className="bg-bg-gray">
                        <TableRow>
                            <TableCell>Migration status</TableCell>
                            <TableCell>Vendor code</TableCell>
                            <TableCell>First name</TableCell>
                            <TableCell>Last name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {usersFound.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-gray-500">
                                    No user found in Azure or VCP. Complete the details below to
                                    create both.
                                </TableCell>
                            </TableRow>
                        ) : (
                            usersFound.map((user) => (
                                <TableRow key={getUserKey(user)}>
                                    <TableCell
                                        style={userStatusStyles[user.migrationStatus ?? 'VCP']}
                                    >
                                        {user.migrationStatus}
                                    </TableCell>
                                    <TableCell>{user.userGroup?.vendorId ?? '-'}</TableCell>
                                    <TableCell>{user.firstName ?? '-'}</TableCell>
                                    <TableCell>{user.lastName ?? '-'}</TableCell>
                                    <TableCell>{user.email ?? '-'}</TableCell>
                                    <TableCell>
                                        {userExistsInAzure(user) && !userExistsInVcp(user) ? (
                                            <button
                                                type="button"
                                                onClick={createUser}
                                                disabled={isCreating}
                                                className="bg-black text-white px-3 py-2 text-sm hover:opacity-80 transition disabled:opacity-50"
                                            >
                                                Create in VCP
                                            </button>
                                        ) : userExistsInVcp(user) &&
                                          !userExistsInAzure(user) &&
                                          shouldCreateAzureUser ? (
                                            <button
                                                type="button"
                                                onClick={createUser}
                                                disabled={isCreating}
                                                className="bg-black text-white px-3 py-2 text-sm hover:opacity-80 transition disabled:opacity-50"
                                            >
                                                Create in Azure
                                            </button>
                                        ) : userExistsInAzure(user) && userExistsInVcp(user) ? (
                                            'User already exists in Azure and VCP'
                                        ) : (
                                            'User already exists in VCP'
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            )}

            {usersFound !== null && (needsAzureUser || needsVcpUser) && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
                        <TextField
                            label="First name"
                            value={firstName}
                            onChange={(_, value) => setFirstName(value ?? '')}
                        />
                        <TextField
                            label="Last name"
                            value={lastName}
                            onChange={(_, value) => setLastName(value ?? '')}
                        />
                        {needsExternalVcpUser && (
                            <>
                                <TextField
                                    label="Vendor code"
                                    value={vendorId}
                                    onChange={(_, value) => setVendorId(value ?? '')}
                                />
                                <TextField label="Vendor name" value={vendorName} disabled />
                            </>
                        )}
                        {shouldCreateAzureUser && (
                            <TextField
                                label="Initial password"
                                type="password"
                                value={initialPassword}
                                placeholder="Example: Abcd1234"
                                onChange={(_, value) => setInitialPassword(value ?? '')}
                            />
                        )}
                    </div>
                    {shouldCreateAzureUser && (
                        <label className="flex items-center gap-2">
                            <Checkbox
                                checked={forceChangePasswordNextSignIn}
                                onChange={(_, checked) =>
                                    setForceChangePasswordNextSignIn(checked ?? false)
                                }
                            />
                            Force password change at next sign-in
                        </label>
                    )}
                </>
            )}

            {usersFound !== null && usersFound.length === 0 && (needsAzureUser || needsVcpUser) && (
                <button
                    type="button"
                    onClick={createUser}
                    disabled={isCreating}
                    className="self-start bg-black text-white px-4 py-2.5 hover:opacity-80 transition"
                >
                    {isCreating
                        ? 'Creating...'
                        : shouldCreateAzureUser && needsVcpUser
                        ? 'Create in Azure and VCP'
                        : shouldCreateAzureUser
                        ? 'Create in Azure'
                        : 'Create in VCP'}
                </button>
            )}
        </UserFormLayout>
    );
};

const UserFormLayout = ({ title, children }: { title: string; children: React.ReactNode }) => {
    const navigate = useNavigate();

    return (
        <div className="py-10">
            <div className="py-6 px-8 flex items-center justify-between sticky top-[80px] bg-white z-10">
                <div className="max-w-80 flex items-center gap-x-5 z-[9999]">
                    <LayersIcon />
                    <div className="flex flex-col gap-y-2 ">
                        <h1 className="text-gray-900 text-2xl font-extrabold">{title}</h1>
                        <p className="text-gray-500 text-sm">User management</p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => navigate(`/${ROUTES.USER_MANAGEMENT}`)}
                    className="bg-white px-4 py-2.5 flex gap-x-2 border border-gray-300 hover:bg-gray-100/70 transition"
                >
                    Back to users
                </button>
            </div>
            <div className="mt-8 px-8 flex flex-col gap-8">{children}</div>
        </div>
    );
};

const UserTextField = ({
    label,
    defaultValue = '',
    disabled = false,
    value,
    onChange,
}: {
    label: string;
    defaultValue?: string;
    disabled?: boolean;
    value?: string;
    onChange?: (value: string) => void;
}) => {
    return (
        <TextField
            label={label}
            disabled={disabled}
            {...(value !== undefined
                ? {
                      value,
                      onChange: onChange
                          ? (_: unknown, newValue?: string) => onChange(newValue ?? '')
                          : undefined,
                      readOnly: !onChange,
                  }
                : {
                      defaultValue,
                  })}
            styles={{
                fieldGroup: {
                    borderColor: '#D0D5DD',
                    minHeight: 40,
                },
            }}
        />
    );
};

const ExistenceTable = ({
    user,
    onCreateAzure,
    onCreateVcp,
    creatingTarget,
}: {
    user: ISecurityUser;
    onCreateAzure: () => void;
    onCreateVcp: () => void;
    creatingTarget: 'azure' | 'vcp' | null;
}) => {
    return (
        <Table
            className="border-x rounded-lg max-w-2xl"
            style={{
                boxShadow:
                    '0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.10)',
            }}
        >
            <TableHead className="bg-bg-gray">
                <TableRow>
                    <TableCell className="border-none w-44"></TableCell>
                    <TableCell className="border-none">Azure B2C</TableCell>
                    <TableCell className="border-none">VCP</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                <TableRow>
                    <TableCell className="font-bold">Existance in</TableCell>
                    <ExistenceCell exists={userExistsInAzure(user)} />
                    <ExistenceCell exists={userExistsInVcp(user)} />
                </TableRow>
                <TableRow>
                    <TableCell className="font-bold">Create user</TableCell>
                    <TableCell>
                        <CreateUserButton
                            disabled={userExistsInAzure(user)}
                            isLoading={creatingTarget === 'azure'}
                            onClick={onCreateAzure}
                        />
                    </TableCell>
                    <TableCell>
                        <CreateUserButton
                            disabled={userExistsInVcp(user)}
                            isLoading={creatingTarget === 'vcp'}
                            onClick={onCreateVcp}
                        />
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    );
};

const ExistenceCell = ({ exists }: { exists: boolean }) => {
    return (
        <TableCell style={exists ? flagStyles.yes : flagStyles.no} className="font-bold">
            {exists ? 'YES' : 'NO'}
        </TableCell>
    );
};

const CreateUserButton = ({
    disabled,
    isLoading,
    onClick,
}: {
    disabled: boolean;
    isLoading: boolean;
    onClick: () => void;
}) => {
    return (
        <button
            type="button"
            disabled={disabled || isLoading}
            onClick={onClick}
            className="bg-black text-white px-4 py-2.5 hover:opacity-80 transition disabled:cursor-not-allowed disabled:opacity-50"
        >
            {isLoading ? 'Creating...' : 'Create user'}
        </button>
    );
};

export default UserManagement;
